import { ButtonAlert } from 'components/ButtonAlert';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ButtonWithTooltip } from 'components/WithTooltip';
import type { ContractChangeStatusEnum } from 'gql/graphql';

import type { CSSProperties } from 'react';
import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeleteAgreementItem } from '../hooks/useDeleteAgreementItem';

export const RemoveAgreement = ({
    currentAgreementId,
    currentAgreementStatus,
    refreshProject,
    isDisabled,
    styles,
    rulesAccessDisabled,
}: {
    currentAgreementId: number;
    currentAgreementStatus: ContractChangeStatusEnum | undefined;
    refreshHandler: () => void;
    refreshProject: () => void;
    isDisabled: boolean;
    styles?: CSSProperties;
    rulesAccessDisabled?: boolean;
}) => {
    const navigate = useNavigate();

    const {
        confirmationDialogOpened,
        openConfirmationDialogHandler,
        submitDeleteHandler,
        cancelDeleteHandler,
        hasError,
        deleteAgreementData,
        deleteAgreementStatus,
        deleteAgreementError,
        closeErrorModalHandler,
        createErrorReportHandler,
    } = useDeleteAgreementItem({
        refreshDataHandler: refreshProject,
        refreshProject,
        isLastItem: true,
    });

    const onOpenDialog = useCallback(() => {
        openConfirmationDialogHandler({
            id: currentAgreementId,
            status: currentAgreementStatus,
        });
    }, [currentAgreementId, currentAgreementStatus, openConfirmationDialogHandler]);

    const onSubmit = useCallback(() => {
        submitDeleteHandler?.();
    }, [submitDeleteHandler]);

    useEffect(() => {
        if (
            deleteAgreementData?.uerDeleteContractChange?.result &&
            deleteAgreementData?.uerDeleteContractChange?.id === currentAgreementId
        ) {
            navigate('..');
        }
    }, [
        currentAgreementId,
        deleteAgreementData?.uerDeleteContractChange?.result,
        deleteAgreementData?.uerDeleteContractChange?.id,
        navigate,
    ]);

    return (
        <>
            {isDisabled ? (
                <ButtonWithTooltip
                    buttonProps={{
                        label: 'Удалить',
                        view: 'secondary',
                        size: 's',
                        style: styles,
                    }}
                    tooltipProps={{
                        mode: 'mouseover',
                        content: 'Удаление возможно только в статусе "На формировании"',
                    }}
                    fakeDisabled
                    basePadding
                />
            ) : (
                <ButtonAlert
                    label="Удалить"
                    size="s"
                    onClick={onOpenDialog}
                    disabled={isDisabled || rulesAccessDisabled}
                />
            )}
            {confirmationDialogOpened && (
                <ConfirmationDialog
                    isOpened={confirmationDialogOpened}
                    onSubmit={onSubmit}
                    onClose={cancelDeleteHandler}
                    textHeader="Удаление доп. соглашения"
                    textConfirmation="Вы уверены, что хотите удалить доп. соглашение?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
            <ErrorHandlingModal
                titleText="Удаление доп. соглашения"
                isSubmitted={hasError}
                statusRequest={deleteAgreementStatus}
                dataRequest={deleteAgreementData?.uerDeleteContractChange}
                errorsRequest={deleteAgreementError}
                handleCloseModalError={closeErrorModalHandler}
                handleCreateReport={createErrorReportHandler}
            />
        </>
    );
};

RemoveAgreement.displayName = 'RemoveAgreement';
